import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Tooltip,
  useTheme,
  Avatar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Menu as MenuIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  AccountCircle as AccountCircleIcon,
  Dashboard as DashboardIcon,
  Chat as ChatIcon,
  Build as BuildIcon,
  AccountTree as AccountTreeIcon,
  Help as HelpIcon,
  Logout as LogoutIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api'; // make sure the path matches your structure
import BrandLogo from './BrandLogo';

function HeaderBar({
  title,
  darkMode,
  onToggleTheme,
  showBackButton = false,
  onBack,
  customActions = null,
  onMoreOptions = null, // Add handler for More Options menu
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [moreOptionsAnchorEl, setMoreOptionsAnchorEl] = useState(null);
  const theme = useTheme();
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      window.history.back();
    }
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMainMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMainMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMoreOptionsOpen = (event) => {
    setMoreOptionsAnchorEl(event.currentTarget);
  };

  const handleMoreOptionsClose = () => {
    setMoreOptionsAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await api.get('/logout');
    } catch (err) {
      console.error('Logout error:', err);
    }

    localStorage.removeItem('tkn');
    localStorage.removeItem('user');
    navigate('/signin');
    handleProfileMenuClose();
  };

  const menuItems = [
    { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
    { label: 'Chat', path: '/chat', icon: <ChatIcon /> },
    { label: 'Form Builder', path: '/formBuilder', icon: <BuildIcon /> },
    {
      label: 'Agentic Flow Builder',
      path: '/agentic-flow',
      icon: <AccountTreeIcon />,
    },
    { label: 'Help & Support', path: '/help', icon: <HelpIcon /> },
  ];

  const handleMenuItemClick = (path) => {
    navigate(path);
    handleMainMenuClose();
  };

  return (
    <AppBar position="fixed" elevation={1} sx={{ zIndex: 1000 }}>
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
        {showBackButton && (
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}

        {/* Main Menu Button */}
        <IconButton
          edge="start"
          color="inherit"
          onClick={handleMainMenuOpen}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>

        {/* Logo and Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <BrandLogo
            size="small"
            orientation="horizontal"
            showTagline={false}
            iconSize={24}
          />
          {title && (
            <Typography variant="h6" component="div" sx={{ ml: 2 }}>
              {title}
            </Typography>
          )}
        </Box>

        {/* Custom Actions */}
        {customActions}

        {/* More Options Menu Button - Hamburger style */}
        {onMoreOptions && (
          <IconButton
            color="inherit"
            onClick={handleMoreOptionsOpen}
            sx={{
              ml: 1,
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <MoreVertIcon />
          </IconButton>
        )}

        {/* Theme Toggle */}
        <Tooltip
          title={
            darkMode ? 'Switch to light mode' : 'Switch to dark mode'
          }
        >
          <IconButton color="inherit" onClick={onToggleTheme}>
            {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
        </Tooltip>

        {/* Profile Menu */}
        <Tooltip title="Account settings">
          <IconButton
            color="inherit"
            onClick={handleProfileMenuOpen}
            sx={{ ml: 1 }}
          >
            <AccountCircleIcon />
          </IconButton>
        </Tooltip>
      </Toolbar>

      {/* Main Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMainMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {menuItems.map((item) => (
          <MenuItem
            key={item.path}
            onClick={() => handleMenuItemClick(item.path)}
            sx={{ minWidth: 200 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {item.icon}
              {item.label}
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* More Options Menu */}
      <Menu
        anchorEl={moreOptionsAnchorEl}
        open={Boolean(moreOptionsAnchorEl)}
        onClose={handleMoreOptionsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {/* Menu items will be passed from parent component */}
        {onMoreOptions && onMoreOptions(handleMoreOptionsClose)}
      </Menu>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleLogout}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LogoutIcon />
            Logout
          </Box>
        </MenuItem>
      </Menu>
    </AppBar>
  );
}

export default HeaderBar;
// This code defines a HeaderBar component that serves as a navigation bar for a web application.
